import { useEffect, useRef, useState } from "react";
import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import ProviderScheduleRow from "@components/scheduling/settings/ProviderScheduleRow";
import {
  fetchClinicProviders, setClinicDefaultProvider, setClinicProviderRole, setClinicProviderSchedule,
} from "@services/clinicProviders";

const ROLE_OPTIONS = [
  { value: "", label: "Unassigned" },
  { value: "dentist", label: "Dentist" },
  { value: "hygienist", label: "Hygienist" },
];

const selectClass =
  "border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

/**
 * The clinic's synced providers: who they are, what they do, who is the
 * default, and which days they work. Lives in the admin clinic drawer
 * because every inbound booking depends on it, not only campaigns.
 *
 * The role decides which pool a caller lands in: a cleaning goes to a
 * hygienist, anything else to a dentist. The PMS type seeds it; a staff
 * choice sticks across syncs until "Use PMS type". Unassigned providers are
 * never pooled, which is where Fathom's duplicate ghost rows belong.
 *
 * Every change saves at once; the drawer's Save button is not involved.
 */
export default function ProvidersEditor({ clinicId }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Which clinic the rows on screen belong to, readable from inside a save
  // that started before a clinic switch and lands after it.
  const clinicRef = useRef(clinicId);

  useEffect(() => {
    let cancelled = false;
    clinicRef.current = clinicId;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setProviders([]);
    if (!clinicId) { setLoading(false); return undefined; }
    (async () => {
      try {
        const rows = await fetchClinicProviders(clinicId);
        if (!cancelled) setProviders(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Could not load the clinic's providers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clinicId]);

  const replaceRow = (updated) =>
    setProviders((rows) => rows.map((r) =>
      r.external_provider_id === updated.external_provider_id ? { ...r, ...updated } : r));

  // Runs one write for the clinic on screen and drops the reply if the
  // admin switched clinics meanwhile.
  const run = async (write, apply) => {
    const forClinic = clinicId;
    setBusy(true);
    setSaveError(null);
    try {
      const result = await write();
      if (clinicRef.current !== forClinic) return;
      apply(result);
    } catch (e) {
      if (clinicRef.current !== forClinic) return;
      setSaveError(e.message || "Could not save the change");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = (p, value) =>
    run(
      () => setClinicProviderRole({ clinicId, externalProviderId: p.external_provider_id, role: value || null }),
      (updated) => { if (updated) replaceRow(updated); },
    );

  const resetRole = (p) =>
    run(
      () => setClinicProviderRole({ clinicId, externalProviderId: p.external_provider_id, reset: true }),
      (updated) => { if (updated) replaceRow(updated); },
    );

  const makeDefault = (p) =>
    run(
      () => setClinicDefaultProvider({ clinicId, externalProviderId: p.external_provider_id }),
      (rows) => { if (rows.length) setProviders(rows); },
    );

  const saveSchedule = (p) => async (weekdays) => {
    const forClinic = clinicId;
    // A rejection needs no clinic-switch guard: the row shows the error
    // itself, and the list is unrendered on a clinic switch.
    const updated = await setClinicProviderSchedule({
      clinicId, externalProviderId: p.external_provider_id, weekdays,
    });
    if (clinicRef.current !== forClinic) return;
    if (updated) replaceRow(updated);
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div>
        <div className="text-sm font-semibold">Providers</div>
        <p className="text-xs text-gray-500">
          Synced from the PMS. The role decides who a caller is booked with: a cleaning goes to a
          hygienist, anything else to a dentist. Unassigned providers are never offered. The default
          is who new or unknown patients are booked with when no role applies. Working days are guessed from the last three months of
          appointments unless set here.
        </p>
      </div>
      <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />
      <ErrorBanner message={saveError} onDismiss={() => setSaveError(null)} />
      {loading && <p className="text-sm text-gray-500">Loading providers…</p>}
      {!loading && !loadError && providers.length === 0 && (
        <p className="text-sm text-gray-500">No providers synced for this clinic yet.</p>
      )}
      {!loading && providers.length > 0 && (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
          {providers.map((p) => (
            <ProviderScheduleRow key={p.external_provider_id} provider={p} disabled={busy}
                                 onSave={saveSchedule(p)}
                                 leading={<RoleControls provider={p} clinicId={clinicId} busy={busy}
                                                        onChangeRole={changeRole} onResetRole={resetRole}
                                                        onMakeDefault={makeDefault} />} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RoleControls({ provider: p, clinicId, busy, onChangeRole, onResetRole, onMakeDefault }) {
  const roleId = `${p.external_provider_id}-role`;
  const defaultId = `${p.external_provider_id}-default`;
  const staffSet = p.role_source === "manual";
  return (
    <>
      {p.specialty && <span className="text-xs text-gray-500">{p.specialty}</span>}
      {p.status && p.status !== "active" && (
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{p.status}</span>
      )}
      <label htmlFor={roleId} className="sr-only">Role for {p.name}</label>
      <select id={roleId} className={selectClass} value={p.role || ""} disabled={busy}
              onChange={(e) => onChangeRole(p, e.target.value)}>
        {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${staffSet ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>
        {staffSet ? "Role set by staff" : "Role from PMS"}
      </span>
      {staffSet && (
        <Button color="light" size="xs" disabled={busy} onClick={() => onResetRole(p)}
                aria-label={`Use PMS type for ${p.name}`}>Use PMS type</Button>
      )}
      <label htmlFor={defaultId} className="flex items-center gap-1 text-xs text-gray-700">
        <input id={defaultId} type="radio" name={`clinic-default-provider-${clinicId}`} checked={!!p.is_default}
               aria-label={`Default provider: ${p.name}`} disabled={busy} onChange={() => onMakeDefault(p)} />
        Default
      </label>
    </>
  );
}
